// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving scrapped assets snapshot.
 *
 * Provides functionality to fetch scrapped assets snapshot based on a specific date.
 * Each response covers a single cost currency: the backend-computed summary stats
 * for that currency plus the assets denominated in it.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import {
  ScrappedAssetSnapshotItem,
  scrappedAssetSnapshotItemSchema,
} from '@/report/api/types';
import { parseArrayInChunks } from '@/utilities/chunked-parse';
import { DateTime } from 'luxon';
import { z } from 'zod';

/** Backend-computed summary stats for a single cost currency's scrapped-asset snapshot. */
export const scrappedAssetCurrencyStatsSchema = z.object({
  isoCode: isoCurrencyCodeSchema,
  assetCount: z.number(),
  assetsWithCompleteAccounting: z.number(),
  assetsWithIncompleteAccounting: z.number(),
  weightedInventoryDays: z.number().nullable(),
  weightedServiceDays: z.number().nullable(),
  totalScrapValue: z.string().nullable(),
  totalBookValueAtScrap: z.string().nullable(),
  totalGainLossOnScrap: z.string().nullable(),
});

export type ScrappedAssetCurrencyStats = z.infer<
  typeof scrappedAssetCurrencyStatsSchema
>;

// The stats live under the response envelope's data field; assets are stripped here
// and validated separately in chunks below.
const scrappedSnapshotStatsResponseSchema = z.object({
  data: scrappedAssetCurrencyStatsSchema,
  metadata: responseMetadataSchema,
});

/** A single currency's scrapped-asset snapshot: backend stats plus its assets. */
export interface ScrappedAssetsSnapshot extends ScrappedAssetCurrencyStats {
  assets: ScrappedAssetSnapshotItem[];
}

// The raw wire response keeps assets as an unparsed array so they can be validated
// in chunks (snapshots can be large); the stats are validated up front.
interface RawScrappedAssetsSnapshotResponse {
  data: ScrappedAssetCurrencyStats & {
    assets: ScrappedAssetSnapshotItem[];
  };
}

interface ScrappedAssetsSnapshotParameters {
  asOfDate: DateTime;
  currency: IsoCurrencyCode;
}

/**
 * Retrieves a snapshot of scrapped assets as of a specific date for a single
 * cost currency.
 *
 * @param params - The parameters for the snapshot request
 * @returns A promise resolving to the single-currency scrapped-asset snapshot
 */
export async function getScrappedAssetsSnapshot(
  params: ScrappedAssetsSnapshotParameters
): Promise<ScrappedAssetsSnapshot> {
  const asOfDateString = params.asOfDate.toISODate();

  const path = '/v1/report/asset/scrapped-snapshot';
  const response = await api.get<RawScrappedAssetsSnapshotResponse>(path, {
    params: {
      asOfDate: asOfDateString,
      currency: params.currency,
    },
  });

  const parsed = await parseResponse(
    scrappedSnapshotStatsResponseSchema,
    response.data,
    { schemaName: 'ScrappedAssetsSnapshotStats', endpoint: path }
  );
  const stats = parsed.data;
  const assets = await parseArrayInChunks(
    response.data.data.assets,
    scrappedAssetSnapshotItemSchema,
    {
      schemaValidation: {
        schemaName: 'ScrappedAssetSnapshotItem',
        endpoint: path,
        extra: {
          asOfDate: asOfDateString,
          isoCode: stats.isoCode,
        },
      },
    }
  );
  return { ...stats, assets };
}
